import React, { lazy } from 'react';
import { DateFieldDefaults } from '@datanomik/core-ui';
import { Debts } from '@datanomik/core-ui/icons';
import { DEBT_CORE_TASK_ID, DEBT_MODULE_TASK_IDS, QUOTATIONS_ENABLED } from './shared/debtModules';
import { DEBTS_OVERVIEW_PATH } from './shared/debtsNavigation';
import { LEGACY_DEBTS_REDIRECTS } from './routing/legacyDebtsRedirects';

const DebtModuleGate = lazy(() =>
  import('./shared/components/DebtModuleGate').then((m) => ({ default: m.DebtModuleGate })),
);

const LiabilityWriteGuard = lazy(() =>
  import('./shared/components/LiabilityWriteGuard').then((m) => ({ default: m.LiabilityWriteGuard })),
);

// "Visão geral": the rebuilt debts home (ex "Visão geral 2"), the module's index route. The older
// unified page it replaced was deleted with the 2026-08-20 gap audit; the insights engine it used
// (debtInsightsEngine + its drawer/modal) stays on disk on purpose — insights come back later.
// Same module shell/tab bar — it is a tab, not a separate screen.
const DebtsOverviewV2Page = lazy(() =>
  import('./overview-v2/DebtsOverviewV2Page').then((m) => ({ default: m.DebtsOverviewV2Page })),
);

// Back-compat for the retired `/debts/overview-2` address: preserves the query string so
// lens/pivot deep-links (`?view=escada`, `?pivot=owner`) still land on the same view instead of
// resetting to the default.
const OverviewV2Redirect = lazy(() =>
  import('./overview-v2/OverviewV2Redirect').then((m) => ({ default: m.OverviewV2Redirect })),
);

const DebtsRedirect = lazy(() =>
  import('./routing/DebtsRedirect').then((m) => ({ default: m.DebtsRedirect })),
);

const MyDebtsPage = lazy(() =>
  import('./my-debts/MyDebtsPage').then((m) => ({ default: m.MyDebtsPage })),
);

const DebtSchedulePage = lazy(() =>
  import('./debt-schedule/DebtSchedulePage').then((m) => ({ default: m.DebtSchedulePage })),
);

const NewDebtPage = lazy(() =>
  import('./new-debt/NewDebtPage').then((m) => ({ default: m.NewDebtPage })),
);

const BankDebtEditRoute = lazy(() =>
  import('./new-debt/BankDebtEditRoute').then((m) => ({ default: m.BankDebtEditRoute })),
);

const MutuosListPage = lazy(() =>
  import('./mutuos/MutuosListPage').then((m) => ({ default: m.MutuosListPage })),
);

const NewMutuoPage = lazy(() =>
  import('./mutuos/NewMutuoPage').then((m) => ({ default: m.NewMutuoPage })),
);

const MutuoDetailPage = lazy(() =>
  import('./mutuos/MutuoDetailPage').then((m) => ({ default: m.MutuoDetailPage })),
);

const CompaniesPage = lazy(() =>
  import('./mutuos/CompaniesPage').then((m) => ({ default: m.CompaniesPage })),
);

const SecuritizationsListPage = lazy(() =>
  import('./securitizations/SecuritizationsListPage').then((m) => ({
    default: m.SecuritizationsListPage,
  })),
);

const NewSecuritizationPage = lazy(() =>
  import('./securitizations/NewSecuritizationPage').then((m) => ({
    default: m.NewSecuritizationPage,
  })),
);

const SecuritizationDetailPage = lazy(() =>
  import('./securitizations/SecuritizationDetailPage').then((m) => ({
    default: m.SecuritizationDetailPage,
  })),
);

const CovenantsOverviewPage = lazy(() =>
  import('./covenants/CovenantsOverviewPage').then((m) => ({
    default: m.CovenantsOverviewPage,
  })),
);

// Debêntures tab (DNKP-3/T2): own routes mirroring the securitizações family (list/new/:id/edit),
// no new backend entity — same debt.debtType=DEBENTURE motor as Bancárias (D-DEB-6).
const DebenturesListPage = lazy(() =>
  import('./debentures/DebenturesListPage').then((m) => ({ default: m.DebenturesListPage })),
);

const ContractDraftsPage = lazy(() =>
  import('./contract-drafts/ContractDraftsPage').then((m) => ({ default: m.ContractDraftsPage })),
);

const NewDebenturePage = lazy(() =>
  import('./debentures/NewDebenturePage').then((m) => ({ default: m.NewDebenturePage })),
);

const DebentureDetailPage = lazy(() =>
  import('./debentures/DebentureDetailPage').then((m) => ({ default: m.DebentureDetailPage })),
);

// SWAP / NDF básico (DIVIDAS_SWAP_NDF_DESIGN_2026-07-31): "Dívidas -> Coberturas".
const HedgesListPage = lazy(() =>
  import('./hedges/HedgesListPage').then((m) => ({ default: m.HedgesListPage })),
);

const NewHedgePage = lazy(() =>
  import('./hedges/NewHedgePage').then((m) => ({ default: m.NewHedgePage })),
);

// hedge-list-link-detail follow-up: the detail screen (instrument + event history + void) — until
// now `hedges` (list) and `hedges/new` were the only two routes.
const HedgeDetailPage = lazy(() =>
  import('./hedges/HedgeDetailPage').then((m) => ({ default: m.HedgeDetailPage })),
);

const QuotationsListRoute = lazy(() =>
  import('./quotations/QuotationRoutes').then((m) => ({ default: m.QuotationsListRoute })),
);

const QuotationFormRoute = lazy(() =>
  import('./quotations/QuotationRoutes').then((m) => ({ default: m.QuotationFormRoute })),
);

const QuotationDetailRoute = lazy(() =>
  import('./quotations/QuotationRoutes').then((m) => ({ default: m.QuotationDetailRoute })),
);

const quotationsElement = (element: React.ReactElement): React.ReactElement => (
  QUOTATIONS_ENABLED ? element : <DebtsRedirect target={{ path: DEBTS_OVERVIEW_PATH }} />
);

const GuaranteesListRoute = lazy(() =>
  import('./guarantees/GuaranteeRoutes').then((m) => ({ default: m.GuaranteesListRoute })),
);

const GuaranteeFormRoute = lazy(() =>
  import('./guarantees/GuaranteeRoutes').then((m) => ({ default: m.GuaranteeFormRoute })),
);

const GuaranteeEditRoute = lazy(() =>
  import('./guarantees/GuaranteeRoutes').then((m) => ({ default: m.GuaranteeEditRoute })),
);

const GuaranteeDetailRoute = lazy(() =>
  import('./guarantees/GuaranteeRoutes').then((m) => ({ default: m.GuaranteeDetailRoute })),
);

const LiabilityClosingsScreen = lazy(() =>
  import('./liability-closings/LiabilityClosingsScreen').then((m) => ({ default: m.LiabilityClosingsScreen })),
);

const DebtsMovementsPage = lazy(() =>
  import('./movements/DebtsMovementsPage').then((m) => ({ default: m.DebtsMovementsPage })),
);

const DebtMaturitiesPage = lazy(() =>
  import('./maturities/DebtMaturitiesPage').then((m) => ({ default: m.DebtMaturitiesPage })),
);

const UpcomingMaturitiesPage = lazy(() =>
  import('./upcoming/UpcomingMaturitiesPage').then((m) => ({ default: m.UpcomingMaturitiesPage })),
);

const DebtCostByIndexerPage = lazy(() =>
  import('./cost/DebtCostByIndexerPage').then((m) => ({ default: m.DebtCostByIndexerPage })),
);

const DebtFundingCostPage = lazy(() =>
  import('./cost/DebtFundingCostPage').then((m) => ({ default: m.DebtFundingCostPage })),
);

const MarketIndexesPage = lazy(() =>
  import('./market-indexes/MarketIndexesPage').then((m) => ({ default: m.MarketIndexesPage })),
);

const DebtsReportsRoute = lazy(() =>
  import('./reports/DebtsReportsRoute').then((m) => ({ default: m.DebtsReportsRoute })),
);

const MODULE_LABEL = 'Dívidas';
const MODULE_DESCRIPTION = 'Gestão de dívidas bancárias e intercompany.';

const buildDebtRoutes = () => [
  {
    path: '',
    key: 'debts',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsOverviewV2Page lens="resumo" />,
    icon: <Debts />,
  },
  {
    path: 'overview-2',
    key: 'debts-overview-2',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    element: <OverviewV2Redirect />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'my-debts',
    key: 'debts-my-debts',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MyDebtsPage />,
    icon: <Debts />,
  },
  {
    path: 'my-debts/new',
    key: 'debts-my-debts-new',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Nova dívida',
    element: <LiabilityWriteGuard action="create"><NewDebtPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'my-debts/:id/edit',
    key: 'debts-my-debts-edit',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Editar dívida',
    element: <LiabilityWriteGuard action="edit"><BankDebtEditRoute /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'my-debts/:id',
    key: 'debts-schedule',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Cronograma',
    element: <DebtSchedulePage />,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos',
    key: 'debts-mutuos',
    taskId: DEBT_MODULE_TASK_IDS.MUTUOS,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MutuosListPage />,
    icon: <Debts />,
  },
  {
    path: 'mutuos/new',
    key: 'debts-mutuos-new',
    taskId: DEBT_MODULE_TASK_IDS.MUTUOS,
    label: 'Novo mútuo',
    element: <LiabilityWriteGuard action="create" moduleTaskId={DEBT_MODULE_TASK_IDS.MUTUOS}><NewMutuoPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos/:id/edit',
    key: 'debts-mutuos-edit',
    taskId: DEBT_MODULE_TASK_IDS.MUTUOS,
    label: 'Editar mútuo',
    element: <LiabilityWriteGuard action="edit" moduleTaskId={DEBT_MODULE_TASK_IDS.MUTUOS}><NewMutuoPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos/:id',
    key: 'debts-mutuos-detail',
    taskId: DEBT_MODULE_TASK_IDS.MUTUOS,
    label: 'Detalhe do mútuo',
    element: <MutuoDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations',
    key: 'debts-securitizations',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <SecuritizationsListPage />,
    icon: <Debts />,
  },
  {
    path: 'securitizations/new',
    key: 'debts-securitizations-new',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Nova securitização',
    element: <LiabilityWriteGuard action="create"><NewSecuritizationPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations/:id/edit',
    key: 'debts-securitizations-edit',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Editar securitização',
    element: <LiabilityWriteGuard action="edit"><NewSecuritizationPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations/:id',
    key: 'debts-securitizations-detail',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Detalhe da securitização',
    element: <SecuritizationDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures',
    key: 'debts-debentures',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebenturesListPage />,
    icon: <Debts />,
  },
  {
    path: 'contract-drafts',
    key: 'debts-contract-drafts',
    taskId: DEBT_MODULE_TASK_IDS.CONTRACT_AI,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <ContractDraftsPage />,
    icon: <Debts />,
  },
  {
    path: 'debentures/new',
    key: 'debts-debentures-new',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Nova debênture',
    element: <LiabilityWriteGuard action="create"><NewDebenturePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures/:id/edit',
    key: 'debts-debentures-edit',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Editar debênture',
    element: <LiabilityWriteGuard action="edit"><NewDebenturePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures/:id',
    key: 'debts-debentures-detail',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Detalhe da debênture',
    element: <DebentureDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'guarantees',
    key: 'debts-guarantees',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <GuaranteesListRoute />,
    icon: <Debts />,
  },
  {
    path: 'guarantees/new',
    key: 'debts-guarantees-new',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Nova fiança',
    element: <LiabilityWriteGuard action="create"><GuaranteeFormRoute /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'guarantees/:id/edit',
    key: 'debts-guarantees-edit',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Editar fiança',
    element: <LiabilityWriteGuard action="edit"><GuaranteeEditRoute /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'guarantees/:id',
    key: 'debts-guarantees-detail',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Detalhe da fiança',
    element: <GuaranteeDetailRoute />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'quotations',
    key: 'debts-quotations',
    taskId: DEBT_MODULE_TASK_IDS.QUOTATIONS,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: quotationsElement(<QuotationsListRoute />),
    icon: <Debts />,
  },
  {
    path: 'quotations/new',
    key: 'debts-quotations-new',
    taskId: DEBT_MODULE_TASK_IDS.QUOTATIONS,
    label: 'Nova cotação',
    element: quotationsElement(
      <LiabilityWriteGuard action="create" moduleTaskId={DEBT_MODULE_TASK_IDS.QUOTATIONS}><QuotationFormRoute /></LiabilityWriteGuard>,
    ),
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'quotations/:id/edit',
    key: 'debts-quotations-edit',
    taskId: DEBT_MODULE_TASK_IDS.QUOTATIONS,
    label: 'Editar cotação',
    element: quotationsElement(
      <LiabilityWriteGuard action="edit" moduleTaskId={DEBT_MODULE_TASK_IDS.QUOTATIONS}><QuotationFormRoute /></LiabilityWriteGuard>,
    ),
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'quotations/:id',
    key: 'debts-quotations-detail',
    taskId: DEBT_MODULE_TASK_IDS.QUOTATIONS,
    label: 'Detalhe da cotação',
    element: quotationsElement(<QuotationDetailRoute />),
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'companies',
    key: 'debts-companies',
    taskId: DEBT_CORE_TASK_ID,
    label: 'Empresas',
    element: <CompaniesPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'covenants',
    key: 'debts-covenants',
    taskId: DEBT_MODULE_TASK_IDS.COVENANTS,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <CovenantsOverviewPage />,
    icon: <Debts />,
  },
  {
    path: 'hedges',
    // Every tab of the unified shell reports the module's own label/description, never its tab
    // name (the tab bar already says "Coberturas"). This route used to call t() here instead —
    // module scope, evaluated on import before i18next finishes initializing, so the app bar
    // rendered an empty title and the whole bar shifted when landing on /debts/hedges.
    key: 'debts-hedges',
    taskId: DEBT_MODULE_TASK_IDS.HEDGES,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <HedgesListPage />,
    icon: <Debts />,
  },
  {
    path: 'hedges/new',
    key: 'debts-hedges-new',
    taskId: DEBT_MODULE_TASK_IDS.HEDGES,
    label: 'Nova cobertura',
    element: <LiabilityWriteGuard action="create" moduleTaskId={DEBT_MODULE_TASK_IDS.HEDGES}><NewHedgePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'hedges/:id',
    key: 'debts-hedges-detail',
    taskId: DEBT_MODULE_TASK_IDS.HEDGES,
    label: 'Detalhe da cobertura',
    element: <HedgeDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'closings',
    key: 'debts-closings',
    taskId: DEBT_MODULE_TASK_IDS.CLOSING,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <LiabilityClosingsScreen />,
    icon: <Debts />,
  },
  {
    path: 'cost/indexer',
    key: 'debts-cost-indexer',
    taskId: DEBT_MODULE_TASK_IDS.CLOSING,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtCostByIndexerPage />,
    icon: <Debts />,
  },
  {
    path: 'cost/funding',
    key: 'debts-cost-funding',
    taskId: DEBT_MODULE_TASK_IDS.CLOSING,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtFundingCostPage />,
    icon: <Debts />,
  },
  {
    path: 'cost/guarantees',
    key: 'debts-cost-guarantees',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsOverviewV2Page lens="garantias" />,
    icon: <Debts />,
  },
  {
    path: 'movements',
    key: 'debts-movements',
    taskId: DEBT_MODULE_TASK_IDS.CLOSING,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsMovementsPage />,
    icon: <Debts />,
  },
  {
    path: 'maturities',
    key: 'debts-maturities',
    taskId: DEBT_MODULE_TASK_IDS.CLOSING,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtMaturitiesPage />,
    icon: <Debts />,
  },
  {
    path: 'upcoming',
    key: 'debts-upcoming',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <UpcomingMaturitiesPage />,
    icon: <Debts />,
  },
  {
    path: 'market-indexes',
    key: 'debts-market-indexes',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MarketIndexesPage />,
    icon: <Debts />,
  },
  {
    path: 'fair-value',
    key: 'debts-fair-value',
    taskId: DEBT_MODULE_TASK_IDS.MARKET,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsOverviewV2Page lens="valorjusto" />,
    icon: <Debts />,
  },
  ...LEGACY_DEBTS_REDIRECTS.map(({ routePath, routeKey, target }) => ({
    path: routePath,
    key: routeKey,
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    element: <DebtsRedirect target={target} />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  })),
  {
    path: 'reports',
    key: 'debts-reports',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsReportsRoute />,
    icon: <Debts />,
  },
  {
    path: 'reports/:taskId',
    key: 'debts-reports-detail',
    taskId: DEBT_CORE_TASK_ID,
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsReportsRoute />,
    icon: <Debts />,
  },
];

const gateByModule = <T extends { taskId: number; element: React.ReactElement }>(route: T): T => (
  route.taskId === DEBT_CORE_TASK_ID
    ? route
    : {
      ...route,
      element: <DebtModuleGate moduleTaskId={route.taskId}>{route.element}</DebtModuleGate>,
    }
);

const withoutDateFloor = <T extends { element: React.ReactElement }>(route: T): T => ({
  ...route,
  element: <DateFieldDefaults min={null}>{route.element}</DateFieldDefaults>,
});

export const getDebtsRoutes = () => buildDebtRoutes().map(gateByModule).map(withoutDateFloor);
